import React, { useCallback, useContext, useMemo } from 'react'
import { CompanyContext } from './CompanyContext'
import { Notification, toast } from 'components/ui'
import {
    useTagList,
    useCreateTag,
    useUpdateTag,
    useToggleTagActive,
} from 'api/hooks/useTags'

/**
 * Thin compatibility shim that preserves the existing `TagContext` consumer
 * interface (`tags`, `isLoadingTags`, `fetchTags`, `createTag`, `updateTag`,
 * `toggleActiveTag`) while delegating fetch + mutate to the React Query hooks.
 */

const TagContext = React.createContext({
    tags: [],
    isLoadingTags: false,
    fetchTags: () => {},
    createTag: () => {},
    updateTag: () => {},
    toggleActiveTag: () => {},
})

const TagProvider = ({ children }) => {
    const { company } = useContext(CompanyContext)

    const {
        data: tagsResp,
        isLoading: isLoadingTags,
        refetch,
    } = useTagList(
        {
            sort: '-id',
            filter: { company_id: company?.id },
        },
        {
            enabled: !!company?.id,
            select: (resp) => resp?.data ?? resp ?? [],
        }
    )

    const tags = tagsResp || []

    const { mutateAsync: createTagMutate } = useCreateTag()
    const { mutateAsync: updateTagMutate } = useUpdateTag()
    const { mutateAsync: toggleTagMutate } = useToggleTagActive()

    const fetchTags = useCallback(() => {
        refetch()
    }, [refetch])

    const createTag = useCallback(
        async (name, color) => {
            try {
                await createTagMutate({
                    name,
                    color,
                    company_id: company?.id,
                    active: 1,
                })
            } catch (err) {
                toast.push(
                    <Notification
                        title={err?.message || String(err)}
                        type="danger"
                    />
                )
            }
        },
        [createTagMutate, company?.id]
    )

    const updateTag = useCallback(
        async (id, name, color) => {
            try {
                await updateTagMutate({ id, name, color })
            } catch (err) {
                toast.push(
                    <Notification
                        title={err?.message || String(err)}
                        type="danger"
                    />
                )
            }
        },
        [updateTagMutate]
    )

    const toggleActiveTag = useCallback(
        async (id) => {
            const current = tags.find((e) => e.id == id)?.active
            try {
                await toggleTagMutate({ id, active: !current })
            } catch (err) {
                toast.push(
                    <Notification
                        title={err?.message || String(err)}
                        type="danger"
                    />
                )
            }
        },
        [toggleTagMutate, tags]
    )

    const value = useMemo(
        () => ({
            tags,
            isLoadingTags,
            fetchTags,
            createTag,
            updateTag,
            toggleActiveTag,
        }),
        [
            tags,
            isLoadingTags,
            fetchTags,
            createTag,
            updateTag,
            toggleActiveTag,
        ]
    )

    return <TagContext.Provider value={value}>{children}</TagContext.Provider>
}

export { TagContext, TagProvider }
